import { HISTORICAL_POINTS_PER_PAGE } from '@/config/query/allVaults'
import { DEFAULT_HISTORICAL_GRANULARITY } from '@/config/query/queryParams'
import type { ChartPeriod } from '@/types/periods'
import { type BorrowMarketPathParams, getInternalAppSdk } from '@/utils/internalAppSdk'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { MINUTE_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { subDays, toUnixTimestamp } from '@vaultsfyi/common/utils'

const borrowMarketHistoricalQueryKey = ['borrowMarketHistorical'] as const

interface BorrowMarketHistoricalDataQueryParams extends BorrowMarketPathParams {
  assetId: string
  chartsPeriod: ChartPeriod
  enabled?: boolean
}

export function borrowMarketHistoricalQueryOptions({
  network,
  marketId,
  assetId,
  chartsPeriod,
  enabled = true,
}: BorrowMarketHistoricalDataQueryParams) {
  return queryOptions({
    queryKey: [...borrowMarketHistoricalQueryKey, { network, marketId, assetId, chartsPeriod }] as const,
    async queryFn({ queryKey }) {
      const { network, marketId, assetId, chartsPeriod } = queryKey[1]
      const internalAppSdk = getInternalAppSdk()
      const fromTimestamp = Math.floor(toUnixTimestamp(subDays(Date.now(), chartsPeriod)))
      const toTimestamp = Math.floor(toUnixTimestamp(Date.now()))

      return await internalAppSdk
        .getBorrowMarketHistoricalData({
          network,
          marketId,
          assetId,
          granularity: DEFAULT_HISTORICAL_GRANULARITY,
          fromTimestamp,
          toTimestamp,
          perPage: HISTORICAL_POINTS_PER_PAGE,
        })
        .then((data) => data.data)
    },
    retry: 2,
    staleTime: 30 * MINUTE_IN_MILLISECONDS,
    refetchOnWindowFocus: false,
    enabled,
  })
}

export function useBorrowMarketHistoricalQuery({
  initialData,
  ...params
}: BorrowMarketHistoricalDataQueryParams & {
  initialData?: ReturnType<typeof borrowMarketHistoricalQueryOptions>['initialData']
}) {
  const {
    data = [],
    isError,
    isFetching,
  } = useQuery({
    ...borrowMarketHistoricalQueryOptions(params),
    initialData,
  })

  return { data, isError, isFetching }
}
